import { useMedia } from '@/common/service/hooks/useMedia';
import { PartnersCard } from '@/entities/Partner/ui/PartnersCard';
import { ICommonItem, IItemDestination } from '@/screens/CommonPage/domain/CommonPage.domain';
import { CardItem } from '../CardItem/CardItem';
import * as S from '../../CommonPage.styles';
import { DropdownArrow } from '@/shared/images/icons/DropdownArrow';
import { ButtonViewMore } from '../ButtonViewMore/ButtonViewMore';
import { Preloader } from '@/common/ui/loaders/Preloader';
import dynamic from 'next/dynamic';
import { DesktopResultProps } from './DesktopResult';
import { useState, useEffect } from 'react';
import { useViewMoreBlogs } from '@/common/app/hooks/useViewMoreBlogs';
import { BottomButton } from '@/common/ui/buttons/BottomButton/BottomButton';

export type ListProps = {
    indexItemOpen?: number;
    selectedItem?: IItemDestination;
    destinations: IItemDestination[];
    type: 'blog' | 'partners';
    noViewMore?: boolean;
    longList?: boolean;
    upgradableList?: boolean;
    openItem: (index: number, el: HTMLElement) => void;
};

const DesktopResult = dynamic<DesktopResultProps>(
    () => import('./DesktopResult').then((module) => module.DesktopResult),
    { ssr: false, loading: () => <Preloader /> }
);

const getListLimit = (itemsLength: number, longList?: boolean) => (longList ? itemsLength : 2);

export const List = ({
    selectedItem,
    indexItemOpen,
    openItem,
    type,
    destinations,
    noViewMore,
    longList,
    upgradableList,
}: ListProps) => {
    const [isDesktop, setIsDesctop] = useState<boolean>(false);
    const isMobileView = useMedia('(max-width: 991px)');

    const updatedList = useViewMoreBlogs(
        selectedItem?.destination?.id || '',
        {
            pager: selectedItem?.destination?.blogPager || '',
            items: selectedItem?.items || [],
        },
        !upgradableList || !selectedItem || type === 'partners'
    );

    useEffect(() => {
        if (!isMobileView) {
            setIsDesctop(true);
        }
    }, [isMobileView]);

    const showViewMoreBtn =
        !!updatedList &&
        !updatedList.isBlogsEnded &&
        !updatedList.isBlogsLoading &&
        type !== 'partners';

    const listToView =
        updatedList && updatedList.isBlogsTouched
            ? (updatedList.blogsList as unknown as ICommonItem[])
            : destinations[indexItemOpen || 0].items.slice(
                  0,
                  getListLimit(destinations[indexItemOpen || 0].items.length, longList)
              );

    return (
        <>
            <S.ListContainer>
                <S.ScrollListContainer>
                    {destinations.map(({ items, destination }, index) => (
                        <div key={destination.id}>
                            <S.MenuSelect
                                key={destination?.slug ?? destination.id}
                                onClick={(e) => {
                                    openItem(index, e.target as HTMLElement);
                                }}
                                isOpen={indexItemOpen === index}
                                isInfinityList={!!updatedList}
                            >
                                <S.MenuHeader isOpen={indexItemOpen === index}>
                                    <S.DestinationName>{destination.name}</S.DestinationName>
                                    <S.CodeTitle>{destination?.state?.code}</S.CodeTitle>
                                    <S.CommonDropdown isOpen={indexItemOpen === index}>
                                        <DropdownArrow />
                                    </S.CommonDropdown>
                                </S.MenuHeader>
                                <S.MobileItemsContainer isOpen={indexItemOpen === index}>
                                    {indexItemOpen === index && (
                                        <>
                                            {listToView.map((item, index) => {
                                                return type !== 'partners' ? (
                                                    <CardItem
                                                        key={item.slug}
                                                        type={type}
                                                        item={item}
                                                    />
                                                ) : (
                                                    <PartnersCard
                                                        key={item.id || index}
                                                        {...item}
                                                    />
                                                );
                                            })}
                                            {}
                                        </>
                                    )}
                                </S.MobileItemsContainer>
                                <S.BtnWrapper>
                                    {indexItemOpen === index &&
                                        items.length > 2 &&
                                        !updatedList && (
                                            <ButtonViewMore selectedItem={selectedItem} />
                                        )}
                                </S.BtnWrapper>
                                {indexItemOpen === index && isMobileView && showViewMoreBtn && (
                                    <S.ViewMoreWrapper>
                                        <BottomButton
                                            title="View More"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                updatedList.viewMoreBlogs();
                                            }}
                                        />
                                    </S.ViewMoreWrapper>
                                )}
                            </S.MenuSelect>
                        </div>
                    ))}
                </S.ScrollListContainer>
            </S.ListContainer>
            <S.DesktopResultWrapper>
                {isDesktop && !!selectedItem && (
                    <DesktopResult
                        selectedItem={selectedItem}
                        type={type}
                        noViewMore={noViewMore}
                        updatedList={
                            updatedList && updatedList.isBlogsTouched ? listToView : undefined
                        }
                    />
                )}
                {showViewMoreBtn && (
                    <S.ViewMoreWrapper>
                        <BottomButton title="View More" onClick={updatedList.viewMoreBlogs} />
                    </S.ViewMoreWrapper>
                )}
                {updatedList && updatedList.isBlogsLoading && <Preloader />}
            </S.DesktopResultWrapper>
        </>
    );
};
